import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider } from './auth/SessionProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { LocationsPage } from './pages/LocationsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentEditorPage } from './pages/DocumentEditorPage';
import { DocumentPreviewPage } from './pages/DocumentPreviewPage';
import { FloorPlansPage } from './pages/FloorPlansPage';
import { FloorPlanViewerPage } from './pages/FloorPlanViewerPage';
import { MeasurementSeriesDetailPage } from './pages/MeasurementSeriesDetailPage';
import { MeasurementSeriesPage } from './pages/MeasurementSeriesPage';
import { TechnicalObjectsPage } from './pages/TechnicalObjectsPage';

/**
 * Route map.
 *
 * `/login` and `/zugriff-verweigert` are public shells (they contain no data),
 * everything else lives behind `ProtectedRoute` and, more importantly, behind
 * server side authorisation in the API.
 */
export function App(): ReactNode {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/zugriff-verweigert" element={<AccessDeniedPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden"
            element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId"
            element={
              <ProtectedRoute>
                <CustomerDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/orte"
            element={
              <ProtectedRoute>
                <LocationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/objekte"
            element={
              <ProtectedRoute>
                <TechnicalObjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/messungen"
            element={
              <ProtectedRoute>
                <MeasurementSeriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/messungen/:seriesId"
            element={
              <ProtectedRoute>
                <MeasurementSeriesDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/plaene"
            element={
              <ProtectedRoute>
                <FloorPlansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/plaene/:planId"
            element={
              <ProtectedRoute>
                <FloorPlanViewerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/dokumente"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/dokumente/:documentId"
            element={
              <ProtectedRoute>
                <DocumentEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:customerId/dokumente/:documentId/vorschau"
            element={
              <ProtectedRoute>
                <DocumentPreviewPage />
              </ProtectedRoute>
            }
          />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
